export type ProgressProps = {
    /**
     * How wide (%) the progress bar/circle should fill.
     */
    percent?: number;

    /**
     * Shows the progress as a percent
     */
    showProgressLabel?: boolean;

    /**
     * Shows a title to explain what the progress is for.
     */
    title?: string;

    /**
     * Variant of progress chart
     */
    type?: 'bar' | 'circle';

    /**
     * Size variant of progress chart
     */
    size?: 'small' | 'regular';
};

function Progress({ title, percent = 0, showProgressLabel = false, type = 'bar', size = 'regular' }: ProgressProps): JSX.Element {
    // STYLES FOR CIRCLE VARIANT
    // Width of the circle stroke
    const strokeWidth = size === 'regular' ? 3 : 2;
    // Size of the enclosing square
    const sqSize = size === 'regular' ? 24 : 16;
    // SVG centers the stroke width on the radius, subtract out so circle fits in square
    const radius = (sqSize - strokeWidth) / 2;
    // Enclose cicle in a circumscribing square
    const viewBox = `0 0 ${sqSize} ${sqSize}`;
    // Arc length at 100% coverage is the circle circumference
    const dashArray = radius * Math.PI * 2;
    // Scale 100% coverage overlay with the actual percent
    const dashOffset = dashArray - (dashArray * percent) / 100;

    return (
        <div>
            {title && <p className="text-xs font-medium text-gray-700 dark:text-gray-400 mb-1">{title}</p>}
            {type === 'bar' && (
                <>
                    <div className="bg-gray-200 dark:bg-white/[0.07] rounded-full overflow-hidden">
                        <div
                            className={`h-2 rounded-full ${
                                percent === 100 ? 'bg-green-600 dark:bg-green-400' : 'bg-indigo-600 dark:bg-indigo-400'
                            }`}
                            style={{ width: `${percent}%` }}
                        />
                    </div>
                    {showProgressLabel && <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">{percent}%</p>}
                </>
            )}
            {type === 'circle' && (
                <div className="flex items-center gap-x-2">
                    {showProgressLabel && <span className="text-xs text-gray-600 dark:text-gray-300">{percent}%</span>}
                    <svg width={sqSize} height={sqSize} viewBox={viewBox}>
                        <circle
                            className="stroke-gray-200 dark:stroke-white/[0.07] fill-none"
                            cx={sqSize / 2}
                            cy={sqSize / 2}
                            r={radius}
                            strokeWidth={`${strokeWidth}px`}
                        />
                        <circle
                            className={`${
                                percent === 100 ? 'stroke-green-600 dark:stroke-green-400' : 'stroke-indigo-600 dark:stroke-indigo-400'
                            } fill-none`}
                            cx={sqSize / 2}
                            cy={sqSize / 2}
                            r={radius}
                            strokeWidth={`${strokeWidth}px`}
                            transform={`rotate(-90 ${sqSize / 2} ${sqSize / 2})`}
                            style={{
                                strokeDasharray: dashArray,
                                strokeDashoffset: dashOffset,
                            }}
                        />
                    </svg>
                </div>
            )}
        </div>
    );
}

export default Progress;
